import { useCallback } from "react"
import { useLocation, useNavigate } from "react-router"

import { RoutesPaths } from "../../../constants/routes.ts"
import { useSurfacePreference } from "../../../hooks/use-surface-preference.ts"
import { surfaceKey } from "../../User/redux/ui-preferences.types.ts"

export type ProjectsView = "grid" | "list"

export const PROJECTS_VIEW_PREFERENCE_KEY = surfaceKey("projects.view")

// The list is the default: it carries more of a project per row and is the view
// most users end up on. The grid remains one click away and, once chosen, is
// remembered — this only decides where a user with no stored preference lands.
export const DEFAULT_PROJECTS_VIEW: ProjectsView = "list"

// The grid reads these off the query string; the list reads none of them. A link
// carrying one is aimed at the grid, whatever the user's stored view.
// Keep in sync with the params ProjectsGridPage reads on mount.
const GRID_FILTER_PARAMS = [
  "project_status",
  "q",
  "project_manager",
  "ordering",
  "created_window",
  "created_after",
  "created_before"
]

export const isGridFilteredLink = (search: string): boolean => {
  const params = new URLSearchParams(search)
  return GRID_FILTER_PARAMS.some((name) => !!params.get(name))
}

export const projectsViewPath = (view: ProjectsView): string =>
  view === "list" ? RoutesPaths.project.list : RoutesPaths.project.grid

export const useProjectsView = (): [ProjectsView, (view: ProjectsView) => void] =>
  useSurfacePreference<ProjectsView>(
    PROJECTS_VIEW_PREFERENCE_KEY,
    DEFAULT_PROJECTS_VIEW
  )

/**
 * Switches Projects to `view` and goes there.
 *
 * The preference is written before navigating, so the resolver at the grid URL
 * reads the new value and stays instead of sending a list-preferring user back.
 */
export const useProjectsViewSwitch = (): ((view: ProjectsView) => void) => {
  const navigate = useNavigate()
  const { search } = useLocation()
  const [, setProjectsView] = useProjectsView()

  return useCallback(
    (view: ProjectsView) => {
      setProjectsView(view)
      // Replace the URL we are leaving only when it is one the resolver would
      // send us straight back to; a filtered link has to stay in history.
      navigate(projectsViewPath(view), { replace: !isGridFilteredLink(search) })
    },
    [navigate, search, setProjectsView]
  )
}
